import { Texture } from 'pixi.js'

/**
 * Artwork cache for decorations.
 *
 * Some things cannot be drawn convincingly in code. A wreath is the clearest
 * example: real greenery is translucent, has depth of field and a thousand tiny
 * shadows, and every attempt to stroke it lands somewhere between clip art and
 * a good illustration. So a decoration can carry a picture instead, and the
 * renderer draws the bulbs on top of it and grades it into the night with
 * everything else.
 *
 * Decoding a data URL is not free, and the renderer runs on every document
 * change, so each image is decoded once and kept. A frame that arrives before
 * the decode finishes simply skips that decoration; the callback below asks for
 * one more redraw when it is ready.
 */

const cache = new Map<string, Texture>()
const pending = new Set<string>()
let onReady: (() => void) | null = null

/** StageView registers this so a late decode still lands on screen. */
export function setAssetReadyCallback(fn: (() => void) | null) {
  onReady = fn
}

/**
 * The texture for this artwork, or null while it is still decoding.
 * Never throws: a broken image just means that decoration does not draw.
 */
export function assetTexture(dataUrl: string): Texture | null {
  const hit = cache.get(dataUrl)
  if (hit) return hit
  if (pending.has(dataUrl)) return null

  pending.add(dataUrl)
  const img = new Image()
  img.onload = () => {
    pending.delete(dataUrl)
    try {
      cache.set(dataUrl, Texture.from(img))
      onReady?.()
    } catch {
      /* undecodable: the decoration stays absent rather than crashing render */
    }
  }
  img.onerror = () => {
    pending.delete(dataUrl)
  }
  img.src = dataUrl
  return null
}

export function clearAssetCache() {
  cache.clear()
  pending.clear()
}
